import { useTranslation } from 'react-i18next';
import './componentDarkSurfaceFixes.css';

const HERO_QUOTE =
  'Sarvalok condenses weeks of manual cross-referencing into seconds. It functions less like a chatbot and more like an elite, rigorously trained research assistant.';
const HERO_EMPHASIS = 'elite, rigorously trained research assistant';

const testimonialDefaults = {
  eyebrow: 'Testimonials',
  heading: 'Trusted by serious seekers.',
  subheading:
    "From tenured academics to lifelong practitioners — here's what users of Sarvalok have to say.",
  heroQuote: HERO_QUOTE,
  heroEmphasis: HERO_EMPHASIS,
  heroName: 'Dr. Amita Sharma',
  heroRole: 'Professor of Religious Studies',
  heroCredentialsLabel: 'What she uses it for',
  heroCredential1: 'Cross-manuscript citation verification',
  heroCredential2: 'Peer-review preparation and commentary comparison',
  heroCredential3: 'Sanskrit source retrieval for academic writing',
  secondary1Quote:
    'Over time, the true meanings of so many profound concepts have been diluted or lost. Sarvalok bypasses modern misinterpretations and retrieves the original, undistorted knowledge directly from the Śāstras. It is a massive leap forward for serious study.',
  secondary1Name: 'Markus Wagner',
  secondary1Role: 'Dedicated Practitioner, Germany',
  secondary2Quote:
    'Manual manuscript research is exhausting when dealing with the sheer volume of classical Indian literature. Sarvalok rapidly searches these libraries to retrieve the philosophical context and related passages I need for my dissertation.',
  secondary2Name: 'Maya Johnson',
  secondary2Role: 'PhD Candidate',
};

function QuoteWithEmphasis({ text, emphasis }: { text: string; emphasis: string }) {
  const index = text.indexOf(emphasis);

  if (!emphasis || index === -1) {
    return <>{text}</>;
  }

  return (
    <>
      {text.slice(0, index)}
      <em className="sl-home-testimonials-emphasis font-medium italic text-gray-950">{emphasis}</em>
      {text.slice(index + emphasis.length)}
    </>
  );
}

export default function HomeTestimonials() {
  const { t } = useTranslation('testimonials');
  const tt = (key: string, defaultValue: string) => String(t(key, { defaultValue }));

  const heroQuote = tt('homepage.hero.quote', testimonialDefaults.heroQuote);
  const heroEmphasis = tt('homepage.hero.emphasis', testimonialDefaults.heroEmphasis);
  const heroCredentials = [
    tt('homepage.hero.credentials.0', testimonialDefaults.heroCredential1),
    tt('homepage.hero.credentials.1', testimonialDefaults.heroCredential2),
    tt('homepage.hero.credentials.2', testimonialDefaults.heroCredential3),
  ];
  const secondaryTestimonials = [
    {
      quote: tt('homepage.secondary.0.quote', testimonialDefaults.secondary1Quote),
      name: tt('homepage.secondary.0.name', testimonialDefaults.secondary1Name),
      role: tt('homepage.secondary.0.role', testimonialDefaults.secondary1Role),
      initials: 'MW',
    },
    {
      quote: tt('homepage.secondary.1.quote', testimonialDefaults.secondary2Quote),
      name: tt('homepage.secondary.1.name', testimonialDefaults.secondary2Name),
      role: tt('homepage.secondary.1.role', testimonialDefaults.secondary2Role),
      initials: 'MJ',
    },
  ];

  return (
    <section
      className="sl-home-testimonials mt-20 border-t border-gray-100 pt-16 md:mt-24 md:pt-20"
      aria-labelledby="home-testimonials-heading"
    >
      <div className="mx-auto max-w-5xl">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <span className="sl-home-testimonials-eyebrow mb-5 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3.5 py-1.5 text-xs font-medium text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" aria-hidden="true" />
            {tt('homepage.eyebrow', testimonialDefaults.eyebrow)}
          </span>
          <h2
            id="home-testimonials-heading"
            className="sl-home-testimonials-heading text-3xl font-bold leading-tight text-gray-950 md:text-4xl"
          >
            {tt('homepage.heading', testimonialDefaults.heading)}
          </h2>
          <p className="sl-home-testimonials-subheading mt-4 text-base leading-relaxed text-gray-600">
            {tt('homepage.subheading', testimonialDefaults.subheading)}
          </p>
        </div>

        <article className="sl-home-testimonials-hero relative grid overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-b from-[#FDFDFF] to-white p-8 text-gray-950 md:grid-cols-[1fr_320px] md:items-center md:gap-12 md:p-14">
          {/* Decorative only: Georgia avoids loading Lora for one oversized quote mark. */}
          <span
            aria-hidden="true"
            className="sl-home-testimonials-quote-mark pointer-events-none absolute left-8 top-6 select-none text-[80px] italic leading-none text-blue-50 md:left-10 md:top-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            &ldquo;
          </span>

          <div className="relative z-10">
            <p className="sl-home-testimonials-quote mb-8 text-[22px] font-normal leading-relaxed text-gray-950 md:text-2xl">
              <QuoteWithEmphasis text={heroQuote} emphasis={heroEmphasis} />
            </p>
            <div className="flex items-center gap-4">
              <div className="sl-home-testimonials-avatar flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gradient-to-br from-blue-50 to-gray-50 text-sm font-semibold text-blue-700">
                AS
              </div>
              <div>
                <p className="sl-home-testimonials-name text-sm font-semibold text-gray-950">
                  {tt('homepage.hero.name', testimonialDefaults.heroName)}
                </p>
                <p className="sl-home-testimonials-role mt-0.5 text-sm text-gray-500">
                  {tt('homepage.hero.role', testimonialDefaults.heroRole)}
                </p>
              </div>
            </div>
          </div>

          <div className="sl-home-testimonials-divider mt-8 border-t border-gray-200 pt-8 md:mt-0 md:border-l md:border-t-0 md:pl-12 md:pt-0">
            <p className="sl-home-testimonials-meta mb-4 text-xs font-medium uppercase tracking-[0.08em] text-gray-500">
              {tt('homepage.hero.credentials_label', testimonialDefaults.heroCredentialsLabel)}
            </p>
            <ul className="space-y-3">
              {heroCredentials.map((credential) => (
                <li key={credential} className="sl-home-testimonials-body flex items-start gap-3 text-sm leading-relaxed text-gray-800">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" aria-hidden="true" />
                  <span>{credential}</span>
                </li>
              ))}
            </ul>
          </div>
        </article>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {secondaryTestimonials.map((testimonial) => (
            <article
              key={testimonial.name}
              className="sl-home-testimonials-secondary flex flex-col rounded-2xl border border-gray-200 bg-white p-8 text-gray-900 transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,11,0.10)]"
            >
              <p className="sl-home-testimonials-body grow text-base font-normal leading-relaxed text-gray-800">{testimonial.quote}</p>
              <div className="sl-home-testimonials-divider mt-7 flex items-center gap-3 border-t border-gray-200 pt-5">
                <div className="sl-home-testimonials-avatar flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 text-xs font-semibold text-gray-600">
                  {testimonial.initials}
                </div>
                <div>
                  <p className="sl-home-testimonials-name text-sm font-semibold text-gray-950">{testimonial.name}</p>
                  <p className="sl-home-testimonials-role mt-0.5 text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
